import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Globe, ShieldCheck, Handshake, Anchor, MapPin, Lightning } from "@phosphor-icons/react";

const HERO_IMG = "https://images.unsplash.com/photo-1735047974891-df59713d8192?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwyfHxjYXJnbyUyMHNoaXAlMjBjb250YWluZXIlMjBwb3J0fGVufDB8fHx8MTc3NzgzNzM0Nnww&ixlib=rb-4.1.0&q=85";
const PORT_IMG = "https://images.unsplash.com/photo-1773126379692-342ff516ea53?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxsb2dpc3RpY3MlMjBmcmVpZ2h0JTIwZ2xvYmFsfGVufDB8fHx8MTc3NzgzNzM0Nnww&ixlib=rb-4.1.0&q=85";

const CORRIDORS = [
  { name: "Adriatic", hubs: "Koper · Trieste · Rijeka" },
  { name: "CEE", hubs: "Ljubljana · Budapest · Prague" },
  { name: "Balkans", hubs: "Belgrade · Sofia · Skopje" },
  { name: "Türkiye", hubs: "Istanbul · Izmir · Mersin" },
  { name: "Middle East", hubs: "Dubai · Jeddah · Doha" },
  { name: "Asia", hubs: "Shanghai · Singapore · Mumbai" },
];

const PILLARS = [
  { icon: ShieldCheck, title: "Curated & vetted", body: "Every member is AI-screened and human-approved. No open-door networks, no noise." },
  { icon: MapPin, title: "Territory protected", body: "Members hold exclusive territory rights by country or region — cooperation, not competition." },
  { icon: Handshake, title: "Built for cooperation", body: "Independent forwarders share real shipments, not promises. Introductions are active, not passive." },
  { icon: Lightning, title: "AI-operated core", body: "Research, scoring, outreach, onboarding and engagement — all powered by an always-on agent layer." },
];

export default function LandingPage() {
  return (
    <>
      {/* HERO */}
      <section className="relative min-h-[78vh] w-full overflow-hidden" data-testid="hero-section">
        <img src={HERO_IMG} alt="Cargo containers" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-navy/70" />
        <div className="absolute inset-0 grid-bg opacity-20" />
        <div className="relative max-w-7xl mx-auto px-6 pt-32 pb-24 grid grid-cols-1 md:grid-cols-12 gap-8 items-end min-h-[78vh]">
          <div className="md:col-span-8 animate-fade-up">
            <div className="flex items-center gap-3 text-ember mb-6">
              <Anchor size={22} weight="duotone" />
              <span className="text-xs font-bold uppercase tracking-[0.3em]">Operated by EP air&amp;sea · Koper · Slovenia</span>
            </div>
            <h1 className="font-display text-white text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
              The curated<br />
              freight forwarding<br />
              network for<br />
              <span className="text-ember">serious operators.</span>
            </h1>
            <p className="mt-8 max-w-xl text-white/80 text-lg leading-relaxed">
              EPAS Global Logistics Network is a selective alliance of independent freight forwarders across
              the Adriatic, Europe, Türkiye, the Middle East and Asia. Territory-protected. AI-operated. Built
              around real cooperation — not directories.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <Link to="/apply" data-testid="hero-apply-cta" className="btn-primary bg-ember hover:bg-ember-600 !text-white">
                Apply for membership <ArrowRight size={16} />
              </Link>
              <Link to="/network" data-testid="hero-network-cta" className="btn-secondary border-white text-white hover:bg-white hover:text-navy">
                See network & territory
              </Link>
            </div>
          </div>
          <div className="md:col-span-4 hidden md:block animate-fade-up">
            <div className="border border-white/20 backdrop-blur-xl bg-white/5 p-6 text-white">
              <div className="overline !text-white/60">Live network</div>
              <div className="mt-3 grid grid-cols-3 gap-4">
                <StatTile label="Members" value="48" />
                <StatTile label="Countries" value="22" />
                <StatTile label="Corridors" value="6" />
              </div>
              <div className="mt-5 text-xs text-white/60 leading-relaxed">
                Agent layer continuously researches, scores and pre-qualifies candidate members across strategic corridors.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="max-w-7xl mx-auto px-6 py-24 sm:py-32" data-testid="pillars-section">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end mb-16">
          <div className="md:col-span-7">
            <div className="overline mb-4">What EPAS is</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-navy tracking-tight">A network for forwarders who actually work together.</h2>
          </div>
          <div className="md:col-span-5">
            <p className="text-navy/70 leading-relaxed">
              Too many networks are membership directories with quarterly dinners. EPAS is built differently — operated by
              <a href="https://www.europacific.si" target="_blank" rel="noreferrer" className="text-navy font-semibold hover:text-ember"> EP air&amp;sea d.o.o.</a> in the Port of Koper since 2014, with an
              AI agent layer that works 24/7 to find, vet and connect members across strategic trade lanes.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {PILLARS.map(({ icon: Icon, title, body }) => (
            <div key={title} className="bg-white border border-black/10 p-6 hover:-translate-y-0.5 hover:shadow-lg transition-all duration-200" data-testid={`pillar-${title.toLowerCase().replace(/\s+/g, "-")}`}>
              <Icon size={26} weight="duotone" className="text-ember mb-4" />
              <div className="font-display font-bold text-navy text-lg mb-2">{title}</div>
              <p className="text-sm text-navy/70 leading-relaxed">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CORRIDORS */}
      <section className="relative py-24 sm:py-32 bg-navy text-white overflow-hidden" data-testid="corridors-section">
        <img src={PORT_IMG} alt="Port" className="absolute inset-0 w-full h-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-navy/70" />
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 mb-16">
            <div className="md:col-span-7">
              <div className="overline !text-ember">Strategic corridors</div>
              <h2 className="font-display text-4xl sm:text-5xl font-bold tracking-tight mt-4">Six corridors. One operating rhythm.</h2>
            </div>
            <div className="md:col-span-5 flex items-end">
              <p className="text-white/70">
                Headquartered in Koper, EPAS builds active cooperation around six high-opportunity corridors where
                independent forwarders outperform integrators on flexibility, local knowledge and project capability.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-px bg-white/10">
            {CORRIDORS.map((c, i) => (
              <div key={c.name} className="bg-navy p-8 hover:bg-navy-800 transition-colors group" data-testid={`corridor-${c.name.toLowerCase()}`}>
                <div className="flex items-start justify-between">
                  <div className="ship-number text-ember text-4xl font-black">0{i + 1}</div>
                  <Globe size={22} weight="duotone" className="text-white/40 group-hover:text-ember transition-colors" />
                </div>
                <div className="mt-6 font-display font-bold text-2xl">{c.name}</div>
                <div className="mt-2 text-white/60 text-sm">{c.hubs}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 py-24 sm:py-32" data-testid="final-cta-section">
        <div className="border border-black/10 bg-white p-10 sm:p-14 grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="overline mb-4">Apply today</div>
            <h2 className="font-display text-4xl sm:text-5xl font-bold text-navy tracking-tight">Selective by design.<br />Reviewed in days, not months.</h2>
            <p className="mt-4 text-navy/70 max-w-2xl">
              Our AI agent layer pre-qualifies your application against service fit, territory and credibility signals.
              A human operator makes the final call. Most approved members are onboarded within 10 business days.
            </p>
          </div>
          <div className="md:col-span-4 flex flex-col gap-3 md:items-end">
            <Link to="/apply" data-testid="cta-apply-button" className="btn-primary">Start application <ArrowRight size={16} /></Link>
            <Link to="/network" data-testid="cta-network-button" className="btn-secondary">See coverage</Link>
          </div>
        </div>
      </section>
    </>
  );
}

const StatTile = ({ label, value }) => (
  <div>
    <div className="ship-number text-3xl font-black text-ember leading-none">{value}</div>
    <div className="mt-1 text-[10px] uppercase tracking-widest text-white/60">{label}</div>
  </div>
);
